import { DateFilterOperation } from '@dbl/core/date';
import {
  FilterStringOptions,
  NUMBER_OPERATION_OPTIONS,
  Operation,
} from '@dbl/core/filter';
import { ChartType } from '@dbl/core/chart';
import { moment2StringEx } from '@/pages/datalab/components/SortableDrag/utils';
import type { IColumnInfo, IOptionType } from '@dbl/core/dataSource';
import {
  checkNullString,
  getLastDateRange,
  getNullTextOfFilter,
  getStaticDateText,
} from '@/pages/datalab/utils/utils';
import { ChartType } from '@dbl/core/chart';

export abstract class DBColumnType {
  // ==========================begin==========================

  static getBaseType(columnType: string | undefined) {
    const instance: DBColumnType = DBColumnType.getType(columnType);
    return instance.getBaseType();
  }
  static getFilterOperation(column: IColumnInfo): string {
    if (!checkNullString(column.operation)) {
      return column.operation || '';
    }
    const instance: DBColumnType = DBColumnType.getType(column.column_type);
    return instance.getFilterDefaultOperation();
  }

  static getFilterValues(column: IColumnInfo): any[] {
    const instance: DBColumnType = DBColumnType.getType(column.column_type);
    return instance.getFilterValues(column);
  }

  static getMetricAggregate(column: IColumnInfo, chartType?: string): any {
    if (!checkNullString(column.aggregate)) {
      return column.aggregate;
    }
    if (column.column_sensitive) {
      return 'distinct';
    }
    const instance: DBColumnType = DBColumnType.getType(column.column_type);
    return instance.getMetricAggregate(column, chartType);
  }

  static getFilterText(column: IColumnInfo, isEn: boolean): string {
    const instance: DBColumnType = DBColumnType.getType(column.column_type);
    return instance.getFilterText(column, isEn);
  }

  static getType(columnType: string | undefined): DBColumnType {
    return typeSet.find((type) => type.values.includes(columnType as string)) ?? new StringType();
  }

  abstract values: string[];

  // ==========================begin==========================
  getFilterValues(column: IColumnInfo): any[] {
    return column.column_value || [];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getMetricAggregate(column: IColumnInfo): string {
    return 'sum';
  }

  // ==========================begin==========================

  abstract getBaseType(): string;
  abstract getFilterDefaultOperation(): string;
  abstract getFilterText(column: IColumnInfo, isEn: boolean): string;
}

export class NumberType extends DBColumnType {
  values = ['bigint', 'largeint', 'smallint', 'tinyint', 'boolean', 'decimal', 'double', 'float', 'int', 'number', 'percent'];
  getBaseType(): string {
    return 'number';
  }
  getFilterDefaultOperation(): string {
    return 'greater';
  }

  getFilterText(column: IColumnInfo, isEn: boolean): string {
    const afterText = column.column_type === 'percent' ? '%' : '';
    const option = NUMBER_OPERATION_OPTIONS.find((item: IOptionType) => item.value === column.operation);
    let operationText = (isEn ? option?.text_en : option?.text) ?? '';
    if (column.operation === Operation.Between) {
      operationText = `${column.column_value?.[0] ?? ''}${afterText} ~ ${column.column_value?.[1] ?? ''}${afterText}`;
    } else if ((column.operation !== Operation.NotNul) && (column.operation !== Operation.Null))  {
      operationText = `${operationText} ${column.column_value?.[0] ? column.column_value?.[0] + afterText : ''}`;
    }
    return operationText;
  }
}
export class StringType extends DBColumnType {
  values = ['char', 'varchar', 'string'];

  getBaseType(): string {
    return 'string';
  }
  getFilterDefaultOperation(): string {
    return 'include';
  }

  getMetricAggregate(column: IColumnInfo, chartType: string): string {
    if(chartType === ChartType.DimNumberTrend){
      return 'distinct';
    }
    return 'max';
  }

  getFilterText(column: IColumnInfo, isEn: boolean): string {
    let text = FilterStringOptions.find((item: any) => item.value === column.operation)?.[isEn ? 'text_en' : "text"];
    text = text ? `${text}` : '';
    const options_all = column.options_all || [];
    const valuesObj = options_all
      .filter((item: any) => column.column_value?.includes(item.value))
      .map((item: any) => isEn ? (item.display_en || item.display) : item.display);
    let textAfter = '';
    // if (column.isCommonDimension && (column.column_value?.includes(ALL_UNIQUE_VALUE) || column.column_value?.length === 0)) {
    //   textAfter = column.column_value?.length === 0 ? (isEn ? 'All (SUM All Users)': '全部（求和）') : (isEn ? 'All (Unique Users)': '全部（去重）');
    // } else if ((valuesObj.length === column.total || column.isAll) && (!column.isCommonDimension)) {
    //   textAfter = isEn ? 'All': '全部';
    // } else
    
    if (column.isAll) {
      textAfter = isEn ? 'All': '全部';
    } else if (valuesObj.length > 2) {
      textAfter = valuesObj.length.toString();
    } else if (valuesObj.length > 0) {
      textAfter = valuesObj.map((item) => getNullTextOfFilter(item))?.join(',') || '';
    } else {
      textAfter = isEn ? 'Please Select': '请选择';
    }

    return `${text}: ${textAfter}`;
  }

  getFilterValues(column: IColumnInfo): any[] {
    if (column.column_value && column.column_value.length > 0) {
      return column.column_value;
    }
    return column.options_all?.map((item) => item.value) || [];
  }
}
export class DateType extends DBColumnType {
  values = ['date', 'datetime'];

  getBaseType(): string {
    return 'date';
  }

  getFilterDefaultOperation(): string {
    return DateFilterOperation.Recent;
  }

  getFilterValues(column: IColumnInfo): any[] {
    const { dateType } = column;
    if (column.column_value?.length === 2) {
      return typeof column.column_value[0] === 'string'
        ? column.column_value
        : column.column_value.map((item) => moment2StringEx(item, dateType as string));
    }
    if (column.column_value) return column.column_value;
    
    // 设置默认值
    return [`${dateType === 'day' ? 7 : 1}-${dateType}`];
    
    // const endTime = moment();
    // let beginTime: moment.Moment = moment();
    // if (granularity === HOUR_GRANULARITY_VALUE) {
    //   beginTime = beginTime.subtract(24, granularity).startOf(HOUR_GRANULARITY_VALUE);
    // } else if (granularity === DateGranularity.DAY) {
    //   beginTime = beginTime.subtract(6, granularity);
    // } else if (granularity === WEEK_GRANULARITY_VALUE) {
    //   beginTime = beginTime.startOf(WEEK_GRANULARITY_VALUE);
    // } else if (granularity === DateGranularity.MONTH) {
    //   beginTime = beginTime.startOf(DateGranularity.MONTH);
    // }
    // return [moment2StringEx(beginTime, granularity), moment2StringEx(endTime, granularity)];
  }

  getFilterText(column: IColumnInfo): string {
    return column.column_text || (column.operation === DateFilterOperation.Recent ? getStaticDateText(getLastDateRange(column.lastCount as string, column.dateType as string, column.from), column.dateType as string) : `${column.column_value ? getStaticDateText(column.column_value as [string, string], column.dateType as string) : ''}`);
    // let text = '';
    // const granularity = column.column_granularity;
    // if (granularity === 'day') {
    //   text = `${text}: ${column.column_value ? column.column_value.join('-') : ''}`;
    // } else {
    //   const day =
    //     granularity && column.column_value
    //       ? string2Moment(column.column_value[1]).diff(string2Moment(column.column_value[0]), granularity)
    //       : '';
    //   text = `最近${day}${granularity === 'month' ? '月' : granularity === 'day' ? '日' : '周'}`;
    // }
    // return text;
  }
  
  getMetricAggregate(column: IColumnInfo, chartType: string): string {
    if(chartType === ChartType.DimNumberTrend){
      return 'distinct';
    }
    return 'max';
  }
}

export const numberType = new NumberType();
export const stringType = new StringType();
export const dateType = new DateType();
const typeSet: DBColumnType[] = [numberType, stringType, dateType];

export interface ErrorDrag {
  [key: string]: string;
}

export interface DragState {
  currentDrag: string;
  errorDrag: ErrorDrag;
  currentDragItem?: any;
}

/** Sortable 拖拽上下文（依赖 DragState，留组件层）。 */
export interface ISortableOption {
  canDrag: boolean;
  dragState: DragState;
  dragMoveHandle: Function;
  dispatch: Function;
}
